<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import * as Sentry from '@sentry/vue';
import FormSelect from 'v3/components/Form/Select.vue';

const props = defineProps({
  value: {
    type: String,
    required: true,
    validator: value =>
      ['ding', 'bell', 'chime', 'magic', 'ping'].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);
const { t } = useI18n();

const alertTones = computed(() => [
  {
    value: 'ding',
    label: t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TONES.DING'),
  },
  {
    value: 'bell',
    label: t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TONES.BELL'),
  },
  {
    value: 'chime',
    label: t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TONES.CHIME'),
  },
  {
    value: 'magic',
    label: t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TONES.MAGIC'),
  },
  {
    value: 'ping',
    label: t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TONES.PING'),
  },
]);

const selectedValue = computed({
  get: () => props.value,
  set: value => {
    emit('change', value);
  },
});

const audio = new Audio();

const playAudio = async () => {
  try {
    // Has great support https://caniuse.com/mdn-api_htmlaudioelement
    audio.src = `/audio/dashboard/${selectedValue.value}.mp3`;
    await audio.play();
  } catch (error) {
    Sentry.captureException(error);
  }
};
</script>

<template>
  <div class="flex items-center gap-2">
    <FormSelect
      v-model="selectedValue"
      name="alertTone"
      spacing="compact"
      class="flex-grow"
      :value="selectedValue"
      :options="alertTones"
      :label="label"
    >
      <option
        v-for="tone in alertTones"
        :key="tone.label"
        :value="tone.value"
        :selected="tone.value === selectedValue"
      >
        {{ tone.label }}
      </option>
    </FormSelect>
    <button
      v-tooltip.top="
        $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.PLAY')
      "
      type="button"
      :aria-label="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.PLAY')"
      class="border-0 shadow-sm outline-none flex justify-center items-center size-11 appearance-none rounded-xl ring-n-weak ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-n-brand flex-shrink-0 mt-[1.75rem]"
      @click="playAudio"
    >
      <Icon icon="i-lucide-volume-2" aria-hidden="true" />
    </button>
  </div>
</template>
